import React, { useState } from "react";
import Calendar from "react-calendar";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import { Car, LucideIcon, Truck } from "lucide-react";
import "react-calendar/dist/Calendar.css";
import TaxiIcon from "../assets/icons/taxi.svg?react";
import { Helmet } from "react-helmet-async";
import { ITPStructuredData } from "./structuredData.ts";
import ImageCarousel from "../components/imageCarousel.tsx";
import SelectedDateSlots from "../components/selectedDateSlots.tsx";

interface TaxCardProps {
  title: string;
  description: string;
  Icon?: LucideIcon;
  renderedIcon?: React.ReactNode;
}
const images = [
  "https://ik.imagekit.io/gabylenautoservice/itp/hala1.webp?updatedAt=1739734767037",
  "https://ik.imagekit.io/gabylenautoservice/itp/IMG_1461.webp?updatedAt=1739734767043",
  "https://ik.imagekit.io/gabylenautoservice/itp/IMG_1464.webp?updatedAt=1739734767084",
  "https://ik.imagekit.io/gabylenautoservice/itp/IMG_1473.webp?updatedAt=1739734767164",
];

const TaxCard = (props: TaxCardProps) => {
  const { Icon, title, renderedIcon, description } = props;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-100 te flex p-2">
      <div className="min-w-12 min-h-[58px] bg-red-100 rounded-lg flex items-center justify-center">
        {Icon && <Icon className="text-red-600" size={24} />}
        {renderedIcon}
      </div>
      <div className={"ml-2"}>
        <h3 className="text-lg font-semibold">{title}</h3>
        <p className="text-gray-600">{description}</p>
      </div>
    </div>
  );
};

const Inspection = () => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const navigate = useNavigate();
  const hoursRef = React.useRef<HTMLDivElement>(null);

  const handleDateSelect = (date: Date | null) => {
    setSelectedDate(date);
    setSelectedTime(null);

    hoursRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleContinue = () => {
    if (selectedDate && selectedTime) {
      navigate("/rezervare", {
        state: {
          date: format(selectedDate, "yyyy-MM-dd"),
          time: selectedTime,
        },
      });
    }
  };

  // Function to disable weekends
  const tileDisabled = ({ date }: { date: Date }) => {
    const day = date.getDay();
    return day === 0 || day === 6; // 0 is Sunday, 6 is Saturday
  };

  const formatShortWeekday = (_: string | undefined, date: Date) => {
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    return days[date.getDay()];
  };

  return (
    <>
      <Helmet>
        <title>
          Rezervă ITP Online | GabyLen Autoservice & ITP Florești Cluj
        </title>
        <meta
          name="description"
          content="Rezervă-ți ITP-ul rapid și ușor la GabyLen Autoservice din Florești. Oferim inspecție tehnică periodică de calitate la prețuri accesibile. Programări online disponibile."
        />
        <meta
          name="keywords"
          content="rezervare ITP Florești, inspecție tehnică periodică Florești Cluj, ITP Florești, programare ITP online Florești, ITP auto Florești, inspecție tehnică auto Cluj, rezervare ITP Cluj"
        />
        <script type="application/ld+json">
          {JSON.stringify(ITPStructuredData)}
        </script>
      </Helmet>
      <div className="bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">ITP</h1>
            <p>
              Fie că aveți un autoturism, un taxi sau o autoutilitară, noi vă
              putem ajuta cu inspecția tehnică periodică. Vă așteptăm într-o
              stație ITP modernă, cu echipamente de ultimă generație și personal
              prietenos și profesionist. În timpul în care mașina este
              inspectată, vă invităm să serviți o cafea în recepție.
            </p>
            <h2 className="text-2xl font-bold text-gray-900 mb-2 mt-4">
              Tarife:
            </h2>
            <div className="flex flex-wrap gap-4 mb-12 md:gap-8">
              <TaxCard Icon={Car} title="Autovehicule" description="200 lei" />
              <TaxCard
                renderedIcon={<TaxiIcon className={"fill-red-600 w-8 h-8"} />}
                title="Taxi"
                description="150 lei"
              />
              <TaxCard
                Icon={Truck}
                title="Autoutilitare"
                description="220 lei"
              />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-8">
            Fă o programare!
          </h1>
          <div className="grid md:grid-cols-2 gap-12">
            <div className="bg-white py-6 px-6 rounded-lg shadow-sm sm:px-4">
              <h2 className="text-xl font-semibold mb-4">
                Alegeți o dată disponibilă
              </h2>
              <div className="inline-block">
                <Calendar
                  //@ts-expect-error Expected date is always Date or null
                  onChange={handleDateSelect}
                  value={selectedDate}
                  minDate={new Date()}
                  className="w-full"
                  tileDisabled={tileDisabled}
                  formatShortWeekday={formatShortWeekday}
                  view={"month"}
                />
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm" ref={hoursRef}>
              <h3 className="text-xl font-semibold mb-4">Poziții libere</h3>
              <SelectedDateSlots
                selectedDate={selectedDate}
                selectedTime={selectedTime}
                onSlotSelected={setSelectedTime}
              />
              {selectedDate && selectedTime ? (
                <div className="mt-8 text-center">
                  <button
                    onClick={handleContinue}
                    className="min-w-60 bg-red-600 text-white px-8 py-3 rounded-md text-lg hover:bg-red-700"
                  >
                    Rezervă acum
                  </button>
                </div>
              ) : null}
            </div>
          </div>
          <ImageCarousel
            title={"ITP"}
            description={[
              "Stația noastră ITP, deschisă în 2024, este dotată cu echipamente moderne și tehnologii de ultimă generație pentru a asigura verificări rapide și precise. Ne-am propus să transformăm timpul de așteptare într-o experiență plăcută, oferindu-vă o cafea proaspătă și un spațiu confortabil în care să vă relaxați. Vă așteptăm cu servicii de calitate și profesionalism! ☕🚗",
            ]}
            images={images}
            className={"bg-white mt-6 p-4 rounded-lg shadow-sm"}
          />
        </div>
      </div>
    </>
  );
};

export default Inspection;
